import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, LayoutDashboard, Database, Activity, Shield, MessageSquare, Users, Settings, Loader as Loader2, RefreshCw, ExternalLink, ChevronDown, LayoutGrid, ArrowLeft } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from '@/hooks/use-toast';
import { useWorkspace } from '@/context/WorkspaceContext';
import { callSiteApi } from '@/components/Sites/utils';
import { SiteOverview } from '@/components/Sites/overview/SiteOverview';
import { WpUsersTab } from '@/components/Sites/wp-users/WpUsersTab';
import { BackupsTab } from '@/components/Sites/backups/BackupsTab';
import { UptimeMonitorTab } from '@/components/Sites/uptime';
import { SubmissionsTab } from '@/components/Sites/submissions/SubmissionsTab';
import { SiteSettingsTab } from '@/components/Sites/settings/SiteSettingsTab';
import { ContentTab } from '@/components/Sites/content/ContentTab';
import { ComingSoon } from '@/components/Sites/ComingSoon';
import { useUptimeRealtime } from '@/components/Sites/uptime/useUptimeRealtime';
import type { Site, UptimeEvent } from '@/components/Sites/types';

interface SiteDetailsProps {
  isGlobalDarkMode?: boolean;
}

export default function SiteDetails({ isGlobalDarkMode }: SiteDetailsProps) {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspace } = useWorkspace();

  const [site, setSite] = useState<Site | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [clearedAmount, setClearedAmount] = useState<string | null>(null);


  const [wpUsers, setWpUsers] = useState<any[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [uptimeEvents, setUptimeEvents] = useState<UptimeEvent[]>([]);

  const fetchUptimeEvents = async () => {
    if (!siteId) return;
    const { data, error } = await supabase
      .from('workspace_site_uptime_events')
      .select('*')
      .eq('site_id', siteId)
      .order('created_at', { ascending: false })
      .limit(3);
    if (!error && data) setUptimeEvents(data);
  };

  useEffect(() => {
    if (site?.id) fetchUptimeEvents();
  }, [site?.id]);

  useEffect(() => {
    if (!siteId || !currentWorkspace?.id) return;
    const loadSite = async () => {
      setIsLoading(true);
      try {
        // First try with all columns (including new backup columns)
        const { data, error } = await supabase
          .from('workspace_sites')
          .select(`*, last_plugins_data, last_themes_data, last_core_info, last_sync_at, backup_enabled, backup_recurrence, backup_retention, backup_last_run, backup_next_run, backup_next_full_run, backup_time, backup_day, backup_type, incremental_full_recurrence, incremental_recurrence, last_full_backup_key`)
          .eq('id', siteId)
          .eq('workspace_id', currentWorkspace.id)
          .single();

        if (error) {
          if (error.code === 'PGRST100' || error.message.includes('column')) {
             console.warn('Backup columns missing. Please apply database migration.');
             // Fallback to basic query
             const { data: fallbackData } = await supabase
               .from('workspace_sites')
               .select(`*, last_plugins_data, last_themes_data, last_core_info, last_sync_at`)
               .eq('id', siteId)
               .eq('workspace_id', currentWorkspace.id)
               .single();
             if (fallbackData) processSiteData(fallbackData);
          } else {
             throw error;
          }
        } else if (data) {
          processSiteData(data);
        }
      } catch (err) {
        console.error('Failed to load site:', err);
        navigate('/sites');
      } finally {
        setIsLoading(false);
      }
    };

    const processSiteData = (data: any) => {
      setSite({
        id: data.id,
        name: data.name,
        url: data.url,
        status: data.status,
        lastPing: data.last_ping,
        wpVersion: data.wp_version || 'Unknown',
        publicKey: data.public_key,
        secretKey: data.secret_key,
        workspace_id: data.workspace_id,
        created_at: data.created_at,
        thumbnail_url: data.thumbnail_url,
        defaultWpUserId: data.default_wp_user_id ?? null,
        defaultWpUserName: data.default_wp_user_name ?? null,
        last_plugins_data: data.last_plugins_data,
        last_themes_data: data.last_themes_data,
        last_core_info: data.last_core_info,
        last_sync_at: data.last_sync_at,
        uptime_status: data.uptime_status,
        uptime_last_check: data.uptime_last_check,
        uptime_last_up_at: data.uptime_last_up_at,
        uptime_last_down_at: data.uptime_last_down_at,
        uptime_interval: data.uptime_interval,
        uptime_enabled: data.uptime_enabled,
        uptime_notify_emails: data.uptime_notify_emails,
        last_perf_mobile: data.last_perf_mobile,
        last_perf_desktop: data.last_perf_desktop,
        repo_popular_plugins: data.repo_popular_plugins,
        repo_popular_themes: data.repo_popular_themes,
        repo_cache_at: data.repo_cache_at,
        backupEnabled: data.backup_enabled,
        backupRecurrence: data.backup_recurrence,
        backupRetention: data.backup_retention,
        backupLastRun: data.backup_last_run,
        backupNextRun: data.backup_next_run,
        backupNextFullRun: data.backup_next_full_run,
        backupTime: data.backup_time,
        backupDay: data.backup_day,
        backupType: data.backup_type,
        incrementalFullRecurrence: data.incremental_full_recurrence,
        incrementalRecurrence: data.incremental_recurrence,
        lastFullBackupKey: data.last_full_backup_key,
      });
    };
    
    loadSite();
  }, [siteId, currentWorkspace?.id]);

  const handleSiteRealtimeUpdate = useCallback((fields: Record<string, unknown>) => {
    setSite(prev => {
      if (!prev) return null;
      const updates: Partial<Site> = {};
      if (fields.uptime_status !== undefined) updates.uptime_status = fields.uptime_status as Site['uptime_status'];
      if (fields.uptime_last_check !== undefined) updates.uptime_last_check = fields.uptime_last_check as string;
      if (fields.uptime_last_up_at !== undefined) updates.uptime_last_up_at = fields.uptime_last_up_at as string;
      if (fields.uptime_last_down_at !== undefined) updates.uptime_last_down_at = fields.uptime_last_down_at as string;
      if (fields.uptime_enabled !== undefined) updates.uptime_enabled = fields.uptime_enabled as boolean;
      if (fields.uptime_interval !== undefined) updates.uptime_interval = fields.uptime_interval as number;
      if (Object.keys(updates).length === 0) return prev;
      return { ...prev, ...updates };
    });
  }, []);

  const handleNewEvent = useCallback((event: { id: string; site_id: string; status: string; created_at: string }) => {
    setUptimeEvents(prev => [event as UptimeEvent, ...prev].slice(0, 3));
  }, []);

  useUptimeRealtime(site?.id, {
    onSiteUpdate: handleSiteRealtimeUpdate,
    onNewEvent: handleNewEvent,
  });

  useEffect(() => {
    if (site && site.status === 'connected') {
      const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
          const data = await callSiteApi(site.url, site.publicKey, site.secretKey, 'core/users');
          if (data && data.users) {
            setWpUsers(data.users);
          }
        } catch (error) {
          console.error('Failed to fetch site users:', error);
        } finally {
          setLoadingUsers(false);
        }
      };
      fetchUsers();
    }
  }, [site?.id, site?.status]);

  const handleSetDefaultUser = async (user: any) => {
    if (!site) return;
    try {
      const { error } = await supabase
        .from('workspace_sites')
        .update({ default_wp_user_id: user.id, default_wp_user_name: user.display_name })
        .eq('id', site.id);
      if (error) throw error;
      setSite({ ...site, defaultWpUserId: user.id, defaultWpUserName: user.display_name });
    } catch (err: any) {
      console.error('Failed to set default user:', err);
    }
  };

  const handleMagicLogin = async (userId?: number) => {
    if (!site) return;
    try {
      setLoginLoading(true);
      const targetUserId = userId || site.defaultWpUserId;
      const body = targetUserId ? JSON.stringify({ user_id: targetUserId }) : JSON.stringify({});
      
      const data = await callSiteApi(site.url, site.publicKey, site.secretKey, 'magic-login', 'POST', body);
      
      if (data?.success && data?.login_url) {
        window.open(data.login_url, '_blank');
      } else {
        alert(data?.message || 'Failed to generate magic login link.');
      }
    } catch (err: any) {
      alert(err.message || 'Network error during magic login.');
    } finally {
      setLoginLoading(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleClearCache = async () => {
    if (!site) return;
    try {
      setClearingCache(true);
      setClearedAmount(null);
      const data = await callSiteApi(
        site.url, 
        site.publicKey, 
        site.secretKey, 
        'core/clear-cache', 
        'POST'
      );
      
      if (data?.success) {
        if (typeof data.freed_bytes === 'number') {
          setClearedAmount(formatBytes(data.freed_bytes));
          setTimeout(() => setClearedAmount(null), 4000);
        }
        toast({
          title: 'Cache Cleared',
          description: data.message || 'The site cache has been successfully cleared.',
        });
      } else {
        throw new Error(data?.message || 'Failed to clear cache');
      }
    } catch (err: any) {
      console.error('Error clearing cache:', err);
      toast({
        title: 'Error Clearing Cache',
        description: err.message || 'There was a problem communicating with the site.',
        variant: 'destructive',
      });
    } finally {
      setClearingCache(false);
    }
  };

  const currentTabPath = location.pathname.split('/').pop();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard, path: 'overview' },
    { id: 'content', label: 'Content', icon: LayoutGrid, path: 'content' },
    { id: 'backups', label: 'Backups', icon: Database, path: 'backups' },
    { id: 'uptime', label: 'Uptime Monitor', icon: Activity, path: 'uptime' },
    { id: 'health', label: 'Health & Security', icon: Shield, path: 'health' },
    { id: 'entries', label: 'Entries', icon: MessageSquare, path: 'entries' },
    { id: 'wp-users', label: 'WP Users', icon: Users, path: 'wp-users' },
    { id: 'settings', label: 'Site Settings', icon: Settings, path: 'settings' },
  ];

  if (isLoading) {
    return (
      <div 
        className="min-h-screen bg-background flex flex-col animate-pulse"
        style={{
          '--background': isGlobalDarkMode ? '240 6% 10%' : '0 0% 100%',
          '--card': isGlobalDarkMode ? '240 5% 14%' : '0 0% 100%'
        } as React.CSSProperties}
      >
        <div className="border-b border-border bg-card px-6 md:px-10 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl bg-muted flex-shrink-0" />
              <div className="space-y-2">
                <div className="h-5 w-48 bg-muted rounded" />
                <div className="flex gap-2">
                  <div className="h-4 w-20 bg-muted rounded-full" />
                  <div className="h-4 w-32 bg-muted rounded" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="h-9 w-32 bg-muted rounded-lg" />
              <div className="h-9 w-32 bg-muted rounded-lg" />
            </div>
          </div>
          <div className="flex gap-4 mt-6 -mb-2 overflow-x-auto">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-6 w-24 bg-muted rounded-md flex-shrink-0" />
            ))}
          </div>
        </div>
        <div className="flex-1 px-6 md:px-10 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="h-64 bg-muted rounded-2xl" />
              <div className="h-96 bg-muted rounded-2xl" />
            </div>
            <div className="space-y-6">
              <div className="h-48 bg-muted rounded-2xl" />
              <div className="h-64 bg-muted rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!site) return null;

  const urlFormatted = site.url.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  const isLocal = urlFormatted.endsWith('.local') || urlFormatted.includes('localhost') || urlFormatted.startsWith('127.0.0.1');

  return (
    <div 
      className="min-h-screen bg-background flex flex-col"
      style={{
        '--background': isGlobalDarkMode ? '240 6% 10%' : '0 0% 100%',
        '--card': isGlobalDarkMode ? '240 5% 14%' : '0 0% 100%'
      } as React.CSSProperties}
    >
      <Helmet>
        <title>{site.name} | AgencyPlus Sites</title>
      </Helmet>

      <div className="sticky top-0 lg:top-14 z-20 border-b border-border bg-card px-6 md:px-10 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/sites')}
              className="p-2 rounded-lg text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
              title="Back to Sites"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="w-9 h-9 rounded-xl bg-muted/50 border border-border flex items-center justify-center overflow-hidden flex-shrink-0">
              {!isLocal ? (
                <img
                  src={`https://www.google.com/s2/favicons?domain=${urlFormatted}&sz=64`}
                  alt=""
                  className="w-6 h-6 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : null}
              <Globe className={`w-4 h-4 text-muted-foreground ${!isLocal ? 'hidden' : ''}`} />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="font-bold text-foreground text-base truncate">{site.name}</h1>
                <div className="flex items-center gap-2">
                  {site.status !== 'connected' && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 bg-red-100/60 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800/40">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                      Disconnected
                    </span>
                  )}
                  {site.uptime_status === 'down' && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 border border-red-500/20 animate-in fade-in slide-in-from-left-1">
                      <Activity className="w-3 h-3" /> Offline
                    </span>
                  )}
                </div>
              </div>
              <a href={site.url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 truncate">
                {urlFormatted}
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleClearCache}
              disabled={clearingCache}
              className={`flex items-center gap-2 px-4 h-9 rounded-lg border text-sm font-medium transition-colors disabled:opacity-60 ${
                clearedAmount 
                  ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold' 
                  : 'border-border bg-background text-foreground hover:bg-muted/50'
              }`}
            >
              {clearingCache ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Clearing...</>
              ) : clearedAmount ? (
                <><span className="text-emerald-500">✨</span> Cleared {clearedAmount}</>
              ) : (
                <><RefreshCw className="w-4 h-4" /> Clear Cache</>
              )}
            </button>

            <div className="relative flex items-center h-9">
              <button
                onClick={() => handleMagicLogin()}
                disabled={loginLoading || site.status !== 'connected'}
                className="flex items-center gap-2 px-4 h-full rounded-l-lg bg-workspace-accent text-white text-sm font-semibold hover:bg-workspace-accent-hover transition-colors disabled:opacity-60 shadow-sm border-r border-white/10"
              >
                {loginLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>
                )}
                Login
              </button>
              <div className="relative h-full">
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  disabled={site.status !== 'connected'}
                  className="flex items-center justify-center w-8 h-full rounded-r-lg bg-workspace-accent text-white hover:bg-workspace-accent-hover transition-colors disabled:opacity-60 shadow-sm"
                >
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showUserDropdown ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {showUserDropdown && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowUserDropdown(false)} />
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 6 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 6 }}
                        className="absolute right-0 top-full mt-1.5 w-64 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden"
                      >
                        <div className="px-3 py-2 border-b border-border">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Login as</p>
                        </div>
                        <div className="max-h-56 overflow-y-auto">
                          {loadingUsers ? (
                            <div className="flex items-center justify-center py-6">
                              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                            </div>
                          ) : wpUsers.length === 0 ? (
                            <p className="text-xs text-muted-foreground p-5 text-center">No eligible users found</p>
                          ) : (
                            wpUsers.map((user) => (
                              <div key={user.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-muted/50 transition-colors group/row">
                                <img
                                  src={user.avatar_url}
                                  alt=""
                                  className="w-7 h-7 rounded-full flex-shrink-0 bg-muted"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.display_name)}&size=32&background=random`;
                                  }}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-foreground truncate transition-colors">{user.display_name}</p>
                                  <p className="text-[10px] text-muted-foreground capitalize truncate">{user.roles[0] ?? 'administrator'}</p>
                                </div>
                                <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover/row:opacity-100 transition-opacity">
                                  <button
                                    onClick={() => { handleSetDefaultUser(user); setShowUserDropdown(false); }}
                                    title="Set as default"
                                    className={`p-1 rounded transition-colors ${site.defaultWpUserId === user.id ? 'text-yellow-500' : 'text-muted-foreground hover:text-yellow-500'}`}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={site.defaultWpUserId === user.id ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                                  </button>
                                  <button
                                    onClick={() => { handleMagicLogin(user.id); setShowUserDropdown(false); }}
                                    title="Login as this user"
                                    className="p-1 rounded text-muted-foreground hover:text-primary transition-colors"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" /></svg>
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-1 mt-4 -mb-4 overflow-x-auto scrollbar-none">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = currentTabPath === tab.path;
            return (
              <button
                key={tab.id}
                onClick={() => navigate(`/sites/${siteId}/${tab.path}`)}
                className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium transition-all relative whitespace-nowrap flex-shrink-0 ${isActive
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/30 rounded-t-lg'
                  }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
                {isActive && (
                  <motion.div layoutId="site-tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-workspace-accent" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 px-6 md:px-10 py-6">
        <AnimatePresence mode="wait" initial={false}>
          <Routes>
            <Route path="overview" element={
              <SiteOverview
                site={site}
                onUpdateSite={(updates: Partial<Site>) => setSite(prev => prev ? { ...prev, ...updates } : null)}
                uptimeEvents={uptimeEvents}
              />
            } />
            <Route path="content" element={
              <ContentTab
                site={site}
                onUpdateSite={(updates: Partial<Site>) => setSite(prev => prev ? { ...prev, ...updates } : null)}
              />
            } />
            <Route path="backups" element={
              <BackupsTab
                site={site}
                onUpdateSite={(updates: Partial<Site>) => setSite(prev => prev ? { ...prev, ...updates } : null)}
              />
            } />
            <Route path="uptime" element={
              <UptimeMonitorTab
                site={site}
                onUpdateSite={(updates: Partial<Site>) => setSite(prev => prev ? { ...prev, ...updates } : null)}
              />
            } />
            <Route path="health" element={<ComingSoon icon={Shield} label="Health & Security" />} />
            <Route path="entries" element={
              <SubmissionsTab
                site={site}
                onUpdateSite={(updates: Partial<Site>) => setSite(prev => prev ? { ...prev, ...updates } : null)}
              />
            } />
            <Route path="wp-users" element={
              <WpUsersTab
                site={site}
                onUpdateSite={(updates: Partial<Site>) => setSite(prev => prev ? { ...prev, ...updates } : null)}
              />
            } />
            <Route path="settings" element={<SiteSettingsTab site={site} />} />
            <Route path="*" element={<Navigate to="overview" replace />} />
          </Routes>
        </AnimatePresence>
      </div>
    </div>
  );
}
